import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, AlertCircle, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enUS as dfEn, zhCN as dfZh } from "date-fns/locale";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type AdminNotification,
} from "../api/hooks";

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.data?.data ?? [];
  const unreadCount = data?.data?.unreadCount ?? 0;
  // The red dot counts what still needs action — the same rows the list marks "unresolved" — so dot and list agree.
  const unresolvedCount = notifications.filter((n) => !n.resolved_at).length;
  const badgeCount = unresolvedCount > 0 ? unresolvedCount : unreadCount;
  const titleOf = (n: { type: string; title: string; order_code: string }) => (n.type === "delivery_failed" ? t("notifications.deliveryFailedTitle", { code: n.order_code }) : n.title);
  const dateFnsLocale = i18n.language?.startsWith("zh") ? dfZh : dfEn;

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("pointerdown", onPointerDown); document.removeEventListener("keydown", onEsc); };
  }, []);

  const openNotification = (n: AdminNotification) => {
    setOpen(false);
    if (!n.is_read) markRead.mutate(n.id);
    navigate(`/orders/${n.order_id}`, { state: { activeNav: "/orders" } });
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex size-10 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-[#475569] hover:bg-[#f1f5f9] max-[640px]:hidden [&_svg]:size-5"
      >
        <Bell className="size-5" strokeWidth={2.4} aria-hidden="true" />
        {badgeCount > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#dc2626] px-1 text-[10px] font-black leading-none text-white">
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[18px] border border-[#e5e7eb] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.14)]">
          <div className="flex items-center justify-between border-b border-[#f1f5f9] px-4 py-3">
            <span className="text-sm font-black text-[#111827]">{t("notifications.title")}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="cursor-pointer border-none bg-transparent text-xs font-extrabold text-[#ea580c] hover:text-[#c2410c] disabled:opacity-50"
              >
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm font-semibold text-[#867461]">
                {t("notifications.empty")}
              </div>
            ) : (
              notifications.map((n) => {
                const resolved = n.resolved_at != null;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openNotification(n)}
                    className={`flex w-full cursor-pointer items-start gap-3 border-b border-[#f8fafc] px-4 py-3 text-left last:border-b-0 hover:bg-[#fff7ed] ${!n.is_read ? "bg-[#fffaf5]" : "bg-white"}`}
                  >
                    <div className="mt-0.5 shrink-0">
                      {resolved ? (
                        <CheckCircle2 className="size-4 text-[#16a34a]" strokeWidth={2.4} aria-hidden="true" />
                      ) : (
                        <AlertCircle className="size-4 text-[#dc2626]" strokeWidth={2.4} aria-hidden="true" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[11px] font-black uppercase tracking-[0.4px] text-[#867461]">{n.order_code}</span>
                        {!n.is_read && <span className="size-1.5 shrink-0 rounded-full bg-[#ea580c]" />}
                      </div>
                      <div className={`mt-0.5 text-[13px] leading-snug ${!n.is_read ? "font-bold text-[#191c1d]" : "font-semibold text-[#534434]"}`}>
                        {titleOf(n)}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-[#94a3b8]">
                        <span>{formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: dateFnsLocale })}</span>
                        <span className={`font-bold uppercase tracking-[0.4px] ${resolved ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                          {resolved ? t("notifications.resolved") : t("notifications.unresolved")}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
